// Angular
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

// Material library
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';

// Local library
import {
  FormatAmountPipe,
  GeoLocationWebService,
  MatDialogService,
  MatNotificationService,
  NumberWithCommasPipe,
  UnsubscribeObservableService,
TranslatePipe
} from '@amwager/util';
import {
  CommonFacade,
  ENVIRONMENT,
  PermissionService,
  SessionService
} from '@amwager/data-access';
import {
  WagerFacade,
  BetService,
  WagerService,
  RunnerService
} from '@amwager/wager/data-access';
import { AccountFacade } from '@amwager/account/data-access';
import {
  GUEST,
  IGeoLocationModel,
  isOnline$
} from '@amwager/util-ts';
import { DutchComponent } from '../dutch/dutch.component';
import { AbcComponent } from '../abc/abc.component';

// Third party
import { takeUntil } from 'rxjs';

@Component({
  selector: 'feature-mat-bet-preview',
  templateUrl: './bet-preview.component.html',
  styleUrls: ['./bet-preview.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FormatAmountPipe,
    NumberWithCommasPipe,
    MatFormFieldModule,
    MatIconModule,
    TranslatePipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BetPreviewComponent extends
  UnsubscribeObservableService implements OnInit, OnDestroy, OnChanges {

  @Input() selectedEvent: any;
  @Input() selectedRace: any;
  @Input() selectedPool: any;
  @Input() selectedModifier!: string;
  @Input() events: any;
  @Input() selectedAmount!: number;
  @Input() storedSelectedRunners: string = '';
  @Input() showBetPayload: any;
  importData: any;

  // List of error lines mirroring mobile's three stacked `<p>` tags
  // (dutchAbcErrorMsg / cantBet / amount-info). The template loops
  // over this array so the user keeps seeing every reason a wager
  // can't be submitted instead of just the first one.
  errorMessage: string[] = [];
  betDetails: string = '';
  totalAmount: string = '0.00';

  private geoLocation!: IGeoLocationModel;

  dutchEnum: string[] = [
    'None',
    'DutchCost',
    'DutchPayout',
    'ABC'
  ];

  requireConfirmation: boolean = false;
  balanceAmount: number = 0;
  poolMatrix: any = [];
  isClearWager!: boolean | undefined;
  isWagerLimit: boolean = false;
  wagerLimit: number = 0;
  maxWagerTotal: number = 65000;

  isBlockJurisdiction: boolean = false;
  isBlockJurisdictionMsg: string = '';

  isProcessingBet: boolean = false;
  setTimer: any = null;

  isOnline: boolean = true;

  // Sensible default cap used by `toDisplayBetTotalAmount` (and the
  // showBet / calcBets pipeline) until AccountPreferences resolves with
  // the user-specific MaxWagerTotal. Without this, `maxWagerTotal`
  // would start at 0 and the total would be silently clamped to $0
  // because every bet trivially exceeds 0.
  private readonly DEFAULT_MAX_WAGER_TOTAL = 65000;

  constructor(
    private readonly commonFacade: CommonFacade,
    private readonly accountFacade: AccountFacade,
    private readonly wagerFacade: WagerFacade,
    private readonly betService: BetService,
    private readonly wagerService: WagerService,
    private readonly runnerService: RunnerService,
    private readonly sessionService: SessionService,
    private readonly geoLocationWebService: GeoLocationWebService,
    private readonly matNotificationService: MatNotificationService,
    private readonly matDialogService: MatDialogService,
    private readonly permissionService: PermissionService,
    private readonly router: Router,
    // Public so `BetService.resetPreview` / `resetProcessingState`
    // can call `markForCheck()` on it after mutating this component's
    // state (the OnPush template won't re-render otherwise).
    readonly cdRef: ChangeDetectorRef,
    private readonly elementRef: ElementRef,
    @Inject(ENVIRONMENT) readonly environment: string,
  ) {
    super();
  }

  /**
   * True when the staged ticket has at least one runner picked.
   * Drives the "No Runners are selected" prompt in the template.
   * @returns {boolean} True when a runner selection exists
   */
  get hasSelectedRunners(): boolean {
    // A formatted runner string (e.g. "1,2/3-6") means runners exist.
    if (this.storedSelectedRunners || this.importData?.storedSelectedRunners) {
      return true;
    }

    // Otherwise fall back to the raw leg structure: any leg holding
    // at least one runner counts as a selection.
    const legs = this.importData?.storeStructure;
    return Array.isArray(legs)
      && legs.some((leg: any) => Array.isArray(leg?.runners) && leg.runners.length > 0);
  }

  ngOnInit() {
    this.wagerService.checkConnectivity(
      isOnline$, this.environment
    ).subscribe((online: any) => {
      this.isOnline = online;
      this.commonFacade.setIsConnectedInternet('BetPreviewComponent:checkConnectivity', online);
    });

    this.wagerFacade.getTicketGeoLocation$().subscribe((response?: IGeoLocationModel) => {
      if (response === undefined) return;
      this.geoLocation = response;
    });

    this.accountFacade.isGetAccountPreferencesSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((callback) => {

        if (callback === undefined) return;

        const response = callback?.response;
        this.isClearWager = response?.AccountPreferences?.IsClearWager;
        this.requireConfirmation = response?.AccountPreferences?.RequireConfirmation;
        this.isWagerLimit = response?.AccountPreferences?.IsWagerLimit;
        this.wagerLimit = response?.AccountPreferences?.WagerLimit;
        // `||` (not `??`) so an API value of 0 also falls back to a
        // sane default; otherwise the bet preview's total would be
        // clamped to $0 for every positive bet.
        this.maxWagerTotal =
          response?.ToteResponse?.Limits?.MaxWagerTotal || this.DEFAULT_MAX_WAGER_TOTAL;
        this.cdRef.markForCheck();
      });

    this.accountFacade.getBalance$()
      .subscribe((res) => {
        if (res !== undefined) {
          this.balanceAmount = res;
        }
        this.cdRef.markForCheck();
      });

    this.wagerFacade.getRaceMatrix$()
      .subscribe((callback) => {

        if (callback === undefined) return;

        this.poolMatrix = callback;
        this.cdRef.markForCheck();
      });

    // Reset the local processing guard whenever a save/submit bet
    // completes (success or error path). Without this the user would
    // be permanently locked out of the buttons after the first bet
    // because `isProcessingBet` would never flip back to false.
    this.wagerFacade.isSaveBetSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((callback) => {
        if (callback?.isSuccess === undefined) return;
        this.betService.resetProcessingState(this);
      });

    this.wagerFacade.isBetNowSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((callback) => {
        if (callback?.isSuccess === undefined) return;
        this.betService.resetProcessingState(this);
      });

    // this.geoLocationWebService.geo$
    //   .subscribe((geo: { latitude: number; longitude: number } | null) => {
    //     if (geo !== null) {
    //       this.geoLocation = {
    //         CLat: geo.latitude,
    //         CLon: geo.longitude
    //       };
    //     }
    //   });
  }

  /**
   * On changes
   * @param {SimpleChanges} changes - Changes
   * @returns {void} - On changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    // Re-initialize the bet preview when the user truly switches race,
    // pool, or modifier. Compare identities (not object refs) so a
    // same-race pusher refresh — which hands down new refs with the
    // same selection — doesn't wipe the staged preview.
    const prevRace = changes['selectedRace']?.previousValue;
    const currRace = changes['selectedRace']?.currentValue;

    const raceChanged =
      !!changes['selectedRace'] && !changes['selectedRace'].firstChange &&
      `${prevRace?.eventId}-${prevRace?.RaceId}` !== `${currRace?.eventId}-${currRace?.RaceId}`;

    const poolChanged =
      !!changes['selectedPool'] && !changes['selectedPool'].firstChange &&
      changes['selectedPool'].previousValue?.PoolId !== changes['selectedPool'].currentValue?.PoolId;

    const modifierChanged =
      !!changes['selectedModifier'] && !changes['selectedModifier'].firstChange &&
      changes['selectedModifier'].previousValue !== changes['selectedModifier'].currentValue;

    if (raceChanged || poolChanged || modifierChanged) {
      this.betService.resetPreview(this);
    }

    if (changes['showBetPayload']) {
      const payload = changes['showBetPayload'].currentValue;

      if (payload) {
        this.importData = payload;

        // Prefer the per-payload cap when the parent has stamped it
        // (runner.component derives it from the same AccountPreferences
        // call we listen to). Falls back to the local field and the
        // default constant so the total is never silently clamped to
        // $0 when AccountPreferences hasn't resolved yet.
        const effectiveMaxWagerTotal =
          payload.maxWagerTotal ||
          this.maxWagerTotal ||
          this.DEFAULT_MAX_WAGER_TOTAL;

        this.errorMessage = this.betService.toDisplayBetErrorMessage(
          payload,
          // this.selectedPool,
          // this.selectedModifier,
          this.balanceAmount,
          effectiveMaxWagerTotal
        );

        this.betDetails = this.betService.toDisplayBetDetails(
          payload,
          // this.selectedRace,
          // this.selectedPool,
          // this.selectedModifier
        );

        this.totalAmount = this.betService.toDisplayBetTotalAmount(
          payload,
          // this.selectedModifier,
          effectiveMaxWagerTotal
        );
      }
      else {
        // Parent emitted a cleared/undefined payload (race, pool or
        // modifier changed, or a bet was successfully placed with
        // IsClearWager). Wipe the displayed values so the preview
        // visibly reflects the reset.
        this.betService.resetPreview(this);
      }
    }

    this.cdRef.markForCheck();
  }

  /**
   * Confirm bet
   * @param {string} betType - Bet type
   * @returns {Promise<void>} - Confirm bet
   */
  async confirmBet(betType: string): Promise<void> {
    if (!this.isOnline) {
      this.commonFacade.setIsConnectedInternet('BetPreviewComponent:confirmBet', false);
      return;
    }

    if (this.selectedPool === undefined) {
      this.matNotificationService.showAlert('Error', 'Failed to validate ticket.', '', '', 'Close');
      return;
    }

    if (!this.runnerService.hasPermission(this.permissionService, this.router)) {

      // encase
      if (this.sessionService.getUserEmail() === GUEST || !this.sessionService.hasToken()) {
        this.router.navigate(['login']);
      }
      else {
        this.matNotificationService.showAlert(
          'Info', 'You are not an Identified User.', '', '', 'Close'
        );
      }

      return;
    }

    if (this.importData.cantBet) {
      this.matNotificationService.showAlert(
        'Error', 'Failed to validate ticket. Race not Open.', '', '', 'Close'
      );
      return;
    }

    if (!this.selectedAmount || this.selectedAmount < -1) {
      this.matNotificationService.showAlert('Error', 'Please enter valid amount.', '', '', 'Close');
      return;
    }

    // if (
    //   (
    //     this.selectedModifier.toLowerCase() !== 'dutchcost' &&
    //     this.selectedModifier.toLowerCase() !== 'abc'
    //   ) &&
    //   (this.selectedAmount * this.importData.bets) > this.balanceAmount &&
    //   betType !== 'save-bet' &&
    //   (!this.requireConfirmation || this.requireConfirmation)
    // ) {
    //   this.matNotificationService.showAlert('Info', 'Insufficient Funds.', '', 'Deposit Now');
    //   return;
    // }

    this.isProcessingBet = true;

    if (this.setTimer) {
      clearTimeout(this.setTimer);
      this.setTimer = null;
    }

    // Open the confirmation flow immediately. The mobile codebase
    // wraps this in a 1s setTimeout for an Ionic modal-presentation
    // hand-off that web doesn't need; without that delay the X /
    // Close buttons and the submit / save buttons feel instant. The
    // `isProcessingBet` flag still prevents accidental double-clicks.
    {
      if (this.requireConfirmation) {

        if (
          betType !== 'save-bet' &&
          this.selectedModifier.toLowerCase() === 'dutchcost'
        ) {

          const runnersEnmList = this.runnerService.transformLegsRunners(
            this.importData.storeStructure
          );
          const dutchEnum = this.dutchEnum.indexOf(this.selectedModifier);
          // const events = events;
          // const selectedRace = selectedRace;
          // const selectedEvent = selectedEvent;
          // const maxWagerTotal = maxWagerTotal;
          // const isBlockJurisdiction = isBlockJurisdiction;
          // const isBlockJurisdictionMsg = isBlockJurisdictionMsg;

          const dialog = this.matDialogService.open(
            DutchComponent,
            'Dutch Detail',
            {
              eventName: this.selectedEvent.eventName,
              dutchTicket: this.importData.dutchTicket,
              poolMatrix: this.poolMatrix[this.selectedPool?.PoolId]?.response?.ToteResponse,
              baseAmount: this.selectedAmount,
              balanceAmount: this.balanceAmount,
              totalCost: this.importData.dutchTicket.calcAmount(),
              // totalCost: betTotal(),
              pool: this.selectedPool,
              modifier: this.selectedModifier,
              selectedRunners:
                this.storedSelectedRunners ||
                this.importData?.storedSelectedRunners ||
                '',

              // for dynamic total cost & others
              dutchEnum,
              runnersEnmList,
              defaultModifier: 'Straight',
              events: this.events,
              selectedRace: this.selectedRace,
              selectedEvent: this.selectedEvent,
              maxWagerTotal: this.maxWagerTotal,
              isBlockJurisdiction: this.isBlockJurisdiction,
              isBlockJurisdictionMsg: this.isBlockJurisdictionMsg
            },
            true,
            true
          );

          dialog.subscribe((res: any) => {
            if (!res.data) {
              this.betService.resetProcessingState(this);
              return;
            }

            this.betService.betNow(
              res.data.betType,
              res.data,
              this.elementRef,
              this.isClearWager,
              this.importData,
              this.selectedEvent,
              this.selectedRace,
              this.selectedPool,
              this.selectedModifier,
              this.selectedAmount,
              this.geoLocation
            );
          });

          return;
        }
      }

      if (this.requireConfirmation) {

        if (
          betType !== 'save-bet' &&
          this.selectedModifier.toLowerCase() === 'abc'
        ) {

          const dialog = this.matDialogService.open(
            AbcComponent,
            'Abc Detail',
            {
              eventName: this.selectedEvent.eventName,
              abcTicket: this.importData.abcTicket,
              selectedRace: this.selectedRace,
              balanceAmount: this.balanceAmount,
              betNow: async(data: any) => {
                this.betService.betNow(
                  data.betType,
                  data,
                  this.elementRef,
                  this.isClearWager,
                  this.importData,
                  this.selectedEvent,
                  this.selectedRace,
                  this.selectedPool,
                  this.selectedModifier,
                  this.selectedAmount,
                  this.geoLocation
                );
              },
              //   data.betType, data, this.elementRef, this.isClearWager
              // ),
            },
            true,
            true
          );

          dialog.subscribe((res: any) => {
            if (!res.data) {
              this.betService.resetProcessingState(this);
              return;
            }
            this.betService.betNow(
              res.data.betType,
              res.data,
              this.elementRef,
              this.isClearWager,
              this.importData,
              this.selectedEvent,
              this.selectedRace,
              this.selectedPool,
              this.selectedModifier,
              this.selectedAmount,
              this.geoLocation
            );
          });

          return;
        }
      }

      if (this.isWagerLimit) {
        if (this.selectedAmount > this.wagerLimit) {
            const msg = 'Total wagered amount ' +
              this.selectedAmount + ' is greater than the user limit of ' + this.wagerLimit;

            this.matNotificationService.showAlert(
              'Wager Limit',
              msg,
              '',
              'Continue',
              'Close',
              '',
              false,
              { betType }
            ).pipe(takeUntil(this.toUnsubscribe$)).subscribe((confirmed: boolean) => {
              if (!confirmed) {
                this.betService.resetProcessingState(this);
                return;
              }

              this.betService.isRequireConfirmActive(
                betType,
                this.importData,
                this.selectedEvent,
                this.selectedRace,
                this.selectedPool,
                this.selectedModifier,
                this.selectedAmount,
                this.storedSelectedRunners ||
                  this.importData?.storedSelectedRunners ||
                  '',
                this.requireConfirmation,
                this.geoLocation,
                this.elementRef,
                this.isClearWager,
                () => this.betService.resetProcessingState(this)
              );
            });

          return;
        }
      }

      this.betService.isRequireConfirmActive(
        betType,
        this.importData,
        this.selectedEvent,
        this.selectedRace,
        this.selectedPool,
        this.selectedModifier,
        this.selectedAmount,
        this.storedSelectedRunners ||
          this.importData?.storedSelectedRunners ||
          '',
        this.requireConfirmation,
        this.geoLocation,
        this.elementRef,
        this.isClearWager,
        () => this.betService.resetProcessingState(this)
      );

    }

  }

  // /**
  //  * Is require confirm active
  //  * @param {string} betType - Bet type
  //  * @returns {Promise<void>} - Is require confirm active
  //  */
  // async isRequireConfirmActive(betType: string): Promise<void> {
  //   if (this.requireConfirmation) {

  //     const eventId = this.selectedEvent !== undefined ?
  //       this.selectedEvent.ToteResponse.EventDetail.EventId
  //         :
  //       ''
  //     const raceId = this.selectedRace !== undefined ? this.selectedRace.RaceId : '';
  //     const poolId = this.selectedPool !== undefined ? this.selectedPool.PoolId : '';

  //     let details = eventId + '<br>';
  //     details += 'Race '+ raceId + '<br>';
  //     details += '$ ' + this.selectedAmount;
  //     details += poolId + ' ' + this.selectedModifier + ' ' + this.storedSelectedRunners;

  //     // trigger custom modal from race component
  //     // using runner component
  //     this.matNotificationService.showAlert(
  //       betType === 'bet-now' ? 'Place Wager' : 'Save Wager',
  //       details,
  //       '',
  //       'Yes',
  //       'Close',
  //       '',
  //       false,
  //     )
  //   }
  //   else {

  //     if (
  //       this.selectedModifier.toLowerCase() === 'dutchcost'
  //     ) {
  //       this.betNow(
  //         betType,
  //         {
  //           betType,
  //           typeIs: 'dutch',
  //           dutchTicket: this.importData.dutchTicket
  //         }
  //       );
  //     }
  //     else if (
  //       this.selectedModifier.toLowerCase() === 'abc'
  //     ) {
  //       this.betNow(
  //         betType,
  //         {
  //           betType,
  //           typeIs: 'abc',
  //           abcTicket: this.betService.createNewABCTicket(this.importData.abcTicket, true)
  //         }
  //       );
  //     }
  //     else {
  //       this.betNow(betType);
  //     }

  //   }
  // }

  // /**
  //  * Bet now
  //  * @param {string} betType - Bet type
  //  * @param {any} payload - Payload
  //  * @returns {Promise<void>} - Bet now
  //  */
  // async betNow(
  //   betType: string,
  //   payload?: any,
  // ): Promise<void> {

  //   if (this.importData.cantBet) {
  //     this.matNotificationService.showAlert(
  //       'Error',
  //       'Failed to validate ticket. Race not Open.',
  //       '',
  //       '',
  //       'Close'
  //     );
  //     return;
  //   }

  //   let betTicket: IBetTicket = {};

  //   if (betType === 'save-bet') {
  //     betTicket = {
  //       ...this.betTicketPayload,
  //       TicketAmount: (this.selectedAmount ?? 0) * (this.importData.bets ?? 0),
  //       RunId: this.selectedEvent.ToteResponse.RunId,
  //       ToteTicketRequest: {
  //         ...this.betTicketPayload?.ToteTicketRequest,
  //         EventId: this.selectedEvent.ToteResponse.EventDetail.EventId,
  //         RaceId: this.selectedRace.RaceId,
  //         Wagers: [
  //           {
  //             ...this.betTicketPayload?.ToteTicketRequest?.Wagers?.[
  //               0
  //             ],
  //             PoolId: this.selectedPool.PoolId,
  //             Bets: this.importData.bets,
  //             Legs: this.importData.legs,
  //             Modifier: this.runnerService.getModId(this.selectedModifier),
  //             BaseAmount: this.selectedAmount,
  //             Total: this.runnerService.betTotal()
  //           }
  //         ]
  //       }
  //     }

  //     if (payload) {
  //       if (payload.typeIs === 'dutch' && betTicket?.ToteTicketRequest !== undefined) {

  //         const extractWagerData = _.map(
  //           _.filter(payload.dutchTicket.Wagers, (item) => !item.Error),
  //           (item) => _.pick(item, ['PoolId', 'BaseAmount', 'Modifier', 'Legs', 'Bets', 'Total'])
  //         );

  //         const checkWagerModifier = _.forEach(extractWagerData, wager => {
  //           wager.Modifier = (
  //             this.modifierEnam.indexOf(wager.Modifier) < 0 ||
  //             this.modifierEnam.indexOf(wager.Modifier) > 6
  //              ?
  //             0
  //              :
  //             this.modifierEnam.indexOf(wager.Modifier)
  //           )
  //         });

  //         const cleanWagerData = _.forEach(checkWagerModifier, wager => {
  //           _.forEach(wager.Legs, legItem => {
  //             if (_.isArray(legItem.Runners)) {
  //               legItem.Runners = legItem.Runners.join(",");
  //             }
  //           });
  //         });

  //         betTicket.TicketAmount = this.importData.dutchTicket.calcAmount();
  //         betTicket.ToteTicketRequest.Wagers = cleanWagerData;
  //         betTicket.DutchType = payload.dutchTicket.DutchType;
  //         betTicket.DutchProjections = payload.dutchTicket.DutchProjections;

  //         betTicket = {
  //           ...betTicket,
  //           ToteTicketRequest: betTicket.ToteTicketRequest
  //             && (
  //               'ParentStackId' in betTicket.ToteTicketRequest ||
  //               'StackId' in betTicket.ToteTicketRequest
  //             )
  //             ? _.omit(betTicket.ToteTicketRequest, ['ParentStackId', 'StackId'])
  //             : betTicket.ToteTicketRequest
  //         };
  //       }

  //       if (payload.typeIs === 'abc' && betTicket?.ToteTicketRequest !== undefined) {

  //         const extractWagerData = _.map(
  //           _.filter(payload.abcTicket.Wagers, (item) => !item.Error),
  //           (item) => _.pick(item, ['PoolId', 'BaseAmount', 'Modifier', 'Legs', 'Bets', 'Total'])
  //         );

  //         const checkWagerModifier = _.forEach(extractWagerData, wager => {
  //           wager.Modifier = (
  //             this.modifierEnam.indexOf(wager.Modifier) < 0 ||
  //             this.modifierEnam.indexOf(wager.Modifier) > 6
  //              ?
  //             0
  //              :
  //             this.modifierEnam.indexOf(wager.Modifier)
  //           )
  //         });

  //         const cleanWagerData = _.forEach(checkWagerModifier, wager => {
  //           _.forEach(wager.Legs, legItem => {
  //             if (_.isArray(legItem.Runners)) {
  //               legItem.Runners = legItem.Runners.join(",");
  //             }
  //           });
  //         });

  //         betTicket.TicketAmount = 0;
  //         betTicket.ToteTicketRequest.Wagers = cleanWagerData;
  //         betTicket.DutchType = payload.abcTicket.DutchType;
  //       }
  //     }

  //   }
  //   else {
  //     betTicket = {
  //       ...this.betTicketPayload,
  //       TicketAmount: (this.selectedAmount ?? 0) * (this.importData.bets ?? 0),
  //       RunId: this.selectedEvent.ToteResponse.RunId,
  //       ToteTicketRequest: {
  //         ...this.betTicketPayload?.ToteTicketRequest,
  //         EventId: this.selectedEvent.ToteResponse.EventDetail.EventId,
  //         RaceId: this.selectedRace.RaceId,
  //         Wagers: [
  //           {
  //             ...this.betTicketPayload?.ToteTicketRequest?.Wagers?.[
  //               0
  //             ],
  //             PoolId: this.selectedPool.PoolId,
  //             Bets: this.importData.bets,
  //             Legs: this.importData.legs,
  //             Modifier: this.runnerService.getModId(this.selectedModifier),
  //             BaseAmount: this.selectedAmount,
  //             Total: this.runnerService.betTotal()
  //           }
  //         ]
  //       }
  //     }

  //     if (payload) {
  //       if (payload.typeIs === 'dutch' && betTicket?.ToteTicketRequest !== undefined) {

  //         const extractWagerData = _.map(
  //           _.filter(payload.dutchTicket.Wagers, (item) => !item.Error),
  //           (item) => _.pick(item, ['PoolId', 'BaseAmount', 'Modifier', 'Legs', 'Bets', 'Total'])
  //         );

  //         const checkWagerModifier = _.forEach(extractWagerData, wager => {
  //           wager.Modifier = (
  //             this.modifierEnam.indexOf(wager.Modifier) < 0 ||
  //             this.modifierEnam.indexOf(wager.Modifier) > 6
  //              ?
  //             0
  //              :
  //             this.modifierEnam.indexOf(wager.Modifier)
  //           )
  //         });

  //         const cleanWagerData = _.forEach(checkWagerModifier, wager => {
  //           _.forEach(wager.Legs, legItem => {
  //             if (_.isArray(legItem.Runners)) {
  //               legItem.Runners = legItem.Runners.join(",");
  //             }
  //           });
  //         });

  //         betTicket.TicketAmount = this.importData.dutchTicket.calcAmount();
  //         betTicket.ToteTicketRequest.Wagers = cleanWagerData;
  //         betTicket.DutchType = payload.dutchTicket.DutchType;
  //         betTicket.DutchProjections = payload.dutchTicket.DutchProjections;

  //         betTicket = {
  //           ...betTicket,
  //           ToteTicketRequest: betTicket.ToteTicketRequest
  //             && (
  //               'ParentStackId' in betTicket.ToteTicketRequest ||
  //               'StackId' in betTicket.ToteTicketRequest
  //             )
  //             ? _.omit(betTicket.ToteTicketRequest, ['ParentStackId', 'StackId'])
  //             : betTicket.ToteTicketRequest
  //         };

  //       }

  //       if (payload.typeIs === 'abc' && betTicket?.ToteTicketRequest !== undefined) {

  //         const extractWagerData = _.map(
  //           _.filter(payload.abcTicket.Wagers, (item) => !item.Error),
  //           (item) => _.pick(item, ['PoolId', 'BaseAmount', 'Modifier', 'Legs', 'Bets', 'Total'])
  //         );

  //         betTicket.TicketAmount = _.sumBy(extractWagerData, 'Total');

  //         const checkWagerModifier = _.forEach(extractWagerData, wager => {
  //           wager.Modifier = (
  //             this.modifierEnam.indexOf(wager.Modifier) < 0 ||
  //             this.modifierEnam.indexOf(wager.Modifier) > 6
  //              ?
  //             0
  //              :
  //             this.modifierEnam.indexOf(wager.Modifier)
  //           )
  //         });

  //         const cleanWagerData = _.forEach(checkWagerModifier, wager => {
  //           _.forEach(wager.Legs, legItem => {
  //             if (_.isArray(legItem.Runners)) {
  //               legItem.Runners = legItem.Runners.join(",");
  //             }
  //           });
  //         });

  //         betTicket.ToteTicketRequest.Wagers = cleanWagerData;
  //         betTicket.DutchType = payload.abcTicket.DutchType;
  //       }
  //     }

  //   }

  //   if (this.sessionService.getSubscription()) {
  //     betTicket.CustomerId = this.sessionService.getSubscription();
  //   }

  //   if (this.geoLocation === null) {
  //     await this.geoLocationWebService.askPermissionGeoLocation();
  //     const result = await firstValueFrom(this.geoLocationWebService.geo$);

  //     if (result !== null && 'latitude' in result && 'longitude' in result) {
  //       if (result === undefined) return;

  //       if (
  //         (result?.latitude === 0) || (result?.longitude === 0)
  //         || ((result?.latitude as any) === '0') || ((result?.longitude as any) === '0')
  //         || (result?.latitude === 0.0) || (result?.longitude === 0.0)
  //         || ((result?.latitude as any) === '0.0') || ((result?.longitude as any) === '0.0')
  //         || !result?.latitude || !result?.longitude
  //       ) return;

  //       if (result.latitude && result.longitude) {
  //         this.wagerFacade.setGeoLocation({
  //           CLat: result.latitude, CLon: result.longitude
  //         },
  //         'BetPreviewComponent:betNow');

  //           betTicket = {
  //             ...betTicket,
  //             Location: {
  //               ...betTicket?.Location,
  //               CLat: result.latitude,
  //               CLon: result.longitude
  //             }
  //           };
  //       }
  //     }
  //   }
  //   else {
  //     const latitude = this.geoLocation?.CLat || undefined;
  //     const longitude = this.geoLocation?.CLon || undefined;

  //     if (
  //       (latitude === 0) || (longitude === 0)
  //       || ((latitude as any) === '0') || ((longitude as any) === '0')
  //       || (latitude === 0.0) || (longitude === 0.0)
  //       || ((latitude as any) === '0.0') || ((longitude as any) === '0.0')
  //       || !latitude || !longitude
  //     ) return;

  //     if (latitude && longitude) {
  //         betTicket = {
  //           ...betTicket,
  //           Location: {
  //             ...betTicket?.Location,
  //             CLat: latitude,
  //             CLon: longitude
  //           }
  //         };
  //     }
  //   }

  //   // if (betType === 'save-bet')
  //   //   this.betService.execSaveBet(betTicket);
  //   // else
  //   //   this.betService.execBetNow(betTicket);
  // }

  override ngOnDestroy(): void {
    this.geoLocationWebService.stopWatching();
    this.toUnsubscribe();
  }
}
